import { NetworkStatus } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { Box, DateRange, Flex, Heading3, LoadingAnimation } from "@nulogy/components";
import UnstyledLink from "components/common/UnstyledLink";
import Form from "components/controls/Form";
import FormButtons from "components/controls/FormButtons";
import StickyFooter from "components/controls/StickyFooter";
import PageWithBreadcrumbs from "components/pages/PageWithBreadcrumbs";
import paths from "config/routePaths";
import { GetAvailabilityOverrides } from "hooks/apollo/availabilityOverride/availabilityOverride.gql";
import useUpdateAvailabilityOverrides from "hooks/apollo/availabilityOverride/useUpdateAvailabilityOverrides";
import { GetDefaultAvailabilities } from "hooks/apollo/defaultAvailability/defaultAvailability.gql";
import { useNotificationState } from "hooks/state";
import useAvailabilityOverridesUnsavedChangesModal from "hooks/state/useAvailabilityOverridesUnsavedChangesModal";
import filter from "lodash/filter";
import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";
import toLower from "lodash/toLower";
import xorWith from "lodash/xorWith";
import React, { useRef, useState } from "react";
import { useNavigate } from "react-router";
import DateRangeUtil from "utils/DateRange";
import DateTime from "utils/DateTime";
import AvailabilityEmptyState from "./AvailabilityEmptyState";
import AvailabilityOverridesLegend from "./AvailabilityOverridesLegend";
import { AvailabilityOverridesTable } from "./AvailabilityOverridesTable";
import AvailabilityOverridesUnsavedChangesModal from "./AvailabilityOverridesUnsavedChangesModal";

function dayOfWeekName(date) {
  return toLower(DateTime.dayOfWeekName(date));
}

function getDateInFuture(days) {
  const now = new Date();

  return new Date(now.getFullYear(), now.getMonth(), now.getDate() + days);
}

function renderLoading() {
  return (
    <Flex alignItems="center" height="100vh" justifyContent="center" width="100vw">
      <LoadingAnimation />
    </Flex>
  );
}

const isArrayDifferent = (x, y) => !isEmpty(xorWith(x, y, isEqual));

const today = getDateInFuture(0);
const todayInAWeek = getDateInFuture(6);

export default function AvailabilityOverrides() {
  const [dateRange, setDateRange] = useState({
    endDate: todayInAWeek,
    startDate: today,
  });
  const [updateAvailabilityOverrides, { loading: updateAvailabilityOverridesLoading }] =
    useUpdateAvailabilityOverrides();
  const {
    close: closeUnsavedChangesModal,
    open: openUnsavedChangesModal,
    startDate: modalStartDate,
    endDate: modalEndDate,
  } = useAvailabilityOverridesUnsavedChangesModal();

  const { data: defaultAvailabilitiesData, loading: defaultAvailabilitiesLoading } = useQuery(GetDefaultAvailabilities);

  const {
    data: availabilityOverridesData,
    loading: availabilityOverridesLoading,
    refetch: refetchAvailabilityOverrides,
    networkStatus: availabilityOverridesNetworkStatus,
  } = useQuery(GetAvailabilityOverrides, {
    notifyOnNetworkStatusChange: true,
    variables: {
      endDate: DateTime.toISO8601Date(dateRange.endDate),
      startDate: DateTime.toISO8601Date(dateRange.startDate),
    },
  });

  const { setNotification } = useNotificationState();

  if (availabilityOverridesNetworkStatus === NetworkStatus.refetch) return renderLoading();

  if (availabilityOverridesNetworkStatus === NetworkStatus.error) {
    return <h2>There was an error loading this page. Please check your internet connection and refresh.</h2>;
  }

  if (defaultAvailabilitiesLoading || availabilityOverridesLoading || updateAvailabilityOverridesLoading)
    return renderLoading();

  const { defaultAvailabilities } = defaultAvailabilitiesData;
  const { availabilityOverrides } = availabilityOverridesData;

  const handleSave = (formData) => {
    updateAvailabilityOverrides(
      formData.availabilities,
      DateTime.toISO8601Date(dateRange.startDate),
      DateTime.toISO8601Date(dateRange.endDate),
    )
      .then(() => {
        setNotification({
          title: "The schedule’s availability was successfully updated.",
          type: "success",
        });
        if (modalEndDate && modalStartDate) {
          setDateRange({ endDate: modalEndDate, startDate: modalStartDate });
        } else {
          refetchAvailabilityOverrides();
        }
      })
      .catch(() =>
        setNotification({
          title: "The changes were not saved. Please try again.",
          type: "danger",
        }),
      )
      .finally(() => closeUnsavedChangesModal());
  };

  return (
    <PageWithBreadcrumbs
      data-testid="content"
      description={
        <>
          Accommodate day to day variation in availability by overriding the schedule’s{" "}
          <UnstyledLink href={paths.defaultAvailability} style={{ fontWeight: "normal" }}>
            Default availability
          </UnstyledLink>
          .
        </>
      }
      title="Availability overrides"
    >
      <Box position="absolute" right={24}>
        <AvailabilityOverridesLegend />
      </Box>

      <AvailabilityOverridesContent
        availabilityOverrides={availabilityOverrides}
        dateRange={dateRange}
        defaultAvailabilities={defaultAvailabilities}
        handleSave={handleSave}
        openUnsavedChangesModal={openUnsavedChangesModal}
        setDateRange={setDateRange}
      />
    </PageWithBreadcrumbs>
  );
}

function AvailabilityOverridesContent({
  availabilityOverrides,
  defaultAvailabilities,
  dateRange,
  setDateRange,
  handleSave,
  openUnsavedChangesModal,
}) {
  const navigate = useNavigate();
  const currentFormData = useRef(null);
  const [dateRangeError, setDateRangeError] = useState(null);

  const leavePage = () => {
    navigate(paths.home);
  };

  const findOverride = (date, shift, line) =>
    filter(
      availabilityOverrides,
      (override) =>
        DateTime.toISO8601Date(override.overrideDate) === DateTime.toISO8601Date(date) &&
        override.shift.id === shift.id &&
        override.line.id === line.id,
    )[0];

  const dates = DateTime.dateRange(dateRange.startDate, dateRange.endDate);
  const availabilities = [];

  dates.forEach((date) => {
    const dataForDate = defaultAvailabilities.map((defaultAvailability, index) => {
      const disabled = !defaultAvailability.shift[dayOfWeekName(date)];
      const overrideValue = findOverride(date, defaultAvailability.shift, defaultAvailability.line);
      const overridden = overrideValue !== undefined;
      const available = overridden ? overrideValue.available : defaultAvailability[dayOfWeekName(date)];

      return {
        available: !disabled && available,
        date: DateTime.toISO8601Date(date),
        defaultAvailability: defaultAvailability[dayOfWeekName(date)],
        disabled,
        index: availabilities.length + index,
        line: defaultAvailability.line.id,
        shift: defaultAvailability.shift.id,
      };
    });

    availabilities.push(...dataForDate);
  });

  const handleRangeChange = (e) => {
    setDateRangeError(null);

    if (!e.startDate || !e.endDate) return; // Just dont run the query
    if (e.endDate < e.startDate) return; // NDS will set an error and dont run the query

    if (DateTime.differenceInDays(e.startDate, e.endDate) > 14) {
      setDateRangeError("Date range must be 14 days or less");

      return;
    }

    if (isArrayDifferent(availabilities, currentFormData.current.values.availabilities)) {
      openUnsavedChangesModal(e.startDate, e.endDate);
    } else {
      setDateRange({ endDate: e.endDate, startDate: e.startDate });
    }
  };

  const onModalClose = (startDate, endDate) => {
    setDateRange({ endDate, startDate });
  };

  if (defaultAvailabilities.length === 0) {
    return <AvailabilityEmptyState />;
  }

  return (
    <>
      <DateRange
        className="spec-date-range"
        defaultEndDate={dateRange.endDate}
        defaultStartDate={dateRange.startDate}
        disableFlipping={false}
        disableRangeValidation={false}
        errorMessage={dateRangeError}
        mb="x3"
        onRangeChange={handleRangeChange}
        showTimes={false}
      />
      <Heading3 color="darkGrey" fontWeight="normal">
        {DateRangeUtil.getDateRangeMonthText(dateRange)}
      </Heading3>

      <Form enableReinitialize initialValues={{ availabilities }} innerRef={currentFormData} onSave={handleSave}>
        <Flex alignItems="left" style={{ maxWidth: "100vw", overflowX: "scroll", position: "relative" }}>
          <AvailabilityOverridesTable availabilities={availabilities} dates={dates} />
        </Flex>

        <StickyFooter>
          <FormButtons onCancel={leavePage} saveButtonText="Save and apply to schedule" />
        </StickyFooter>

        <AvailabilityOverridesUnsavedChangesModal onClose={onModalClose} />
      </Form>
    </>
  );
}
